import { useEffect, useMemo, useState } from "react";
import "./LivingCast.css";

// Living Cast — public profile main view.
// Renders faction leaderboard, a LIVE indicator strip, a stage with the top
// donor characters bobbing in a colored room with rotating speech bubbles,
// a stats strip, and a placeholder memory-graph panel.
//
// Data wiring per Profile.tsx:
//   donations: { donor_name, donor_handle?, amount, message, faction_id,
//                created_at, source? }
//   factions:  { id, slug, name, color, position, total }
//   stats:     { total_raised, supporter_count, donation_count }

export interface LCDonation {
  id: string;
  donor_name: string;
  donor_handle?: string;
  amount: number;
  message: string | null;
  faction_id: string | null;
  source?: "live" | "profile";
  created_at: number;
}

export interface LCFaction {
  id: string;
  slug: string;
  name: string;
  color: string;
  position: number;
  total: number;
}

export interface LCStats {
  total_raised: number;
  supporter_count: number;
  donation_count: number;
}

interface LivingCastProps {
  donations: LCDonation[];
  factions: LCFaction[];
  stats: LCStats;
}

// Slugs available in /public/overlay-demo/ai-chars/.
const AI_CHARS = ["aria", "boris", "cosma", "drako", "eevy", "fynn", "gala"];

// FNV-1a 32-bit hash for stable character assignment.
function fnv1a(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h >>> 0;
}

function normHandle(d: LCDonation): string {
  const raw = (d.donor_handle ?? d.donor_name ?? "").trim().toLowerCase();
  return raw.replace(/^@/, "");
}

function pickChar(handle: string): string {
  return AI_CHARS[fnv1a(handle || "anon") % AI_CHARS.length];
}

interface CastMember {
  key: string;
  name: string;
  handle: string;
  total: number;
  firstAt: number;
  lastMessage: string | null;
  lastMessageAt: number;
  messages: string[];
  charSlug: string;
}

function buildCast(donations: LCDonation[]): CastMember[] {
  const grouped = new Map<string, CastMember>();
  for (const d of donations) {
    const handle = normHandle(d);
    const key = handle || `id:${d.id}`;
    const ex = grouped.get(key);
    if (ex) {
      ex.total += d.amount;
      if (d.created_at < ex.firstAt) ex.firstAt = d.created_at;
      if (d.message && d.message.trim()) {
        ex.messages.push(d.message);
        if (d.created_at >= ex.lastMessageAt) {
          ex.lastMessage = d.message;
          ex.lastMessageAt = d.created_at;
        }
      }
    } else {
      grouped.set(key, {
        key,
        name: d.donor_name,
        handle,
        total: d.amount,
        firstAt: d.created_at,
        lastMessage: d.message,
        lastMessageAt: d.message ? d.created_at : 0,
        messages: d.message ? [d.message] : [],
        charSlug: pickChar(handle),
      });
    }
  }
  return [...grouped.values()]
    .sort((a, b) => b.total - a.total)
    .slice(0, 5);
}

function daysSince(ts: number): number {
  const diff = Date.now() - ts;
  return Math.max(1, Math.floor(diff / 86_400_000));
}

function formatMoney(n: number): string {
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export default function LivingCast(props: LivingCastProps): JSX.Element {
  const { donations, factions, stats } = props;

  const leaderboard = useMemo(() => {
    return [...factions].sort((a, b) => b.total - a.total).slice(0, 5);
  }, [factions]);

  const supportersByFaction = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const d of donations) {
      if (!d.faction_id) continue;
      const s = m.get(d.faction_id) ?? new Set<string>();
      s.add(normHandle(d));
      m.set(d.faction_id, s);
    }
    return m;
  }, [donations]);

  const cast = useMemo(() => buildCast(donations), [donations]);

  // Stats: tonight's tips (6h window), new chars (24h), reactions/hr, depth.
  const computed = useMemo(() => {
    const now = Date.now();
    const sixH = now - 6 * 3600 * 1000;
    const dayH = now - 24 * 3600 * 1000;
    let tonightCents = 0;
    const newHandlesAll = new Map<string, number>(); // handle -> first seen ts
    for (const d of donations) {
      if (d.created_at >= sixH) tonightCents += d.amount;
      const h = normHandle(d);
      const prev = newHandlesAll.get(h);
      if (prev === undefined || d.created_at < prev) newHandlesAll.set(h, d.created_at);
    }
    let newCharacters = 0;
    for (const ts of newHandlesAll.values()) {
      if (ts >= dayH) newCharacters++;
    }
    const firstTs = donations.length
      ? Math.min(...donations.map((d) => d.created_at))
      : now;
    const hoursSinceFirst = Math.max(1, (now - firstTs) / 3600_000);
    // TODO: replace with a real reactions metric once we track them.
    const reactionsPerHr = Math.round(donations.length / hoursSinceFirst);
    const avgDepth =
      stats.supporter_count > 0
        ? Math.round(stats.donation_count / stats.supporter_count)
        : 0;
    return {
      tonight: tonightCents,
      newCharacters,
      reactionsPerHr,
      avgDepth,
    };
  }, [donations, stats]);

  // Rotate latest messages on the speech bubble of each cast member.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => setTick((n) => n + 1), 8000);
    return () => window.clearInterval(t);
  }, []);
  // Fade-in flag for initial mount.
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setShown(true), 250);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <div className="lc-wrap">
      {/* Faction leaderboard */}
      {leaderboard.length > 0 ? (
        <div className="lc-leaderboard">
          <div className="lc-lb-head">
            <div className="lc-lb-title">Faction Leaderboard</div>
            <div className="lc-lb-count">{leaderboard.length} live</div>
          </div>
          <div className="lc-lb-rows">
            {leaderboard.map((f) => {
              const supporters = supportersByFaction.get(f.id)?.size ?? 0;
              return (
                <div
                  key={f.id}
                  className="lc-lb-row"
                  style={{ ["--lc-chip" as string]: f.color }}
                >
                  <div className="lc-lb-chip" />
                  <div className="lc-lb-name">{f.name}</div>
                  <div className="lc-lb-amt">{formatMoney(f.total)}</div>
                  <div className="lc-lb-supp">· {supporters.toLocaleString()}</div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Cast stage. TODO: wire game title + AI-watching indicator to Twitch Helix; until then we hide them rather than lie. */}
      <div className="lc-stage">
        <div className="lc-stage-bg" />
        <div className="lc-glow" />
        <div className="lc-scan" />

        {cast.length > 0 ? (
          <div className="lc-cast-row">
            {cast.map((c) => {
              const msg =
                c.messages.length > 0
                  ? c.messages[(tick + fnv1a(c.key)) % c.messages.length]
                  : c.lastMessage;
              const day = daysSince(c.firstAt);
              return (
                <div className="lc-cast" key={c.key}>
                  {msg ? (
                    <div className={`lc-bubble${shown ? " lc-show" : ""}`}>
                      <div className="lc-bubble-who">
                        <span>{c.name}</span>
                        <span className="lc-tip">
                          {formatMoney(c.total)} · day {day}
                        </span>
                      </div>
                      <div className="lc-bubble-msg">{msg}</div>
                    </div>
                  ) : null}
                  <div className="lc-cast-body">
                    <img
                      src={`/overlay-demo/ai-chars/${c.charSlug}.png`}
                      alt={c.name}
                      onError={(e) => {
                        // If the char asset is missing, fall back to a glyph.
                        const img = e.currentTarget as HTMLImageElement;
                        img.style.display = "none";
                        const span = img.nextElementSibling as HTMLElement | null;
                        if (span) span.style.display = "grid";
                      }}
                    />
                    <span
                      className="lc-cast-glyph"
                      style={{ display: "none" }}
                    >
                      {(c.name || "?")[0].toUpperCase()}
                    </span>
                  </div>
                  <div className="lc-cast-tag">
                    {c.name} · <span className="lc-amt">{formatMoney(c.total)}</span>
                    <span className="lc-day">day {day}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>

      {/* Stats strip */}
      <div className="lc-strip">
        <div className="lc-stat purple">
          <div className="lc-stat-l">Tonight&apos;s tips</div>
          <div className="lc-stat-v">{formatMoney(computed.tonight)}</div>
        </div>
        <div className="lc-stat gold">
          <div className="lc-stat-l">New characters</div>
          <div className="lc-stat-v">+{computed.newCharacters}</div>
        </div>
        <div className="lc-stat green">
          <div className="lc-stat-l">Reactions / hr</div>
          <div className="lc-stat-v">{computed.reactionsPerHr}</div>
        </div>
        <div className="lc-stat blue">
          <div className="lc-stat-l">Avg memory depth</div>
          <div className="lc-stat-v">{computed.avgDepth}</div>
        </div>
      </div>

      {/* Memory graph placeholder. TODO: wire to real observation feed. */}
      <div className="lc-memory">
        <div className="lc-memory-head">
          <div className="lc-memory-title">Memory Graph</div>
          <div className="lc-memory-pill">
            <span className="lc-eye-dot" /> live
          </div>
        </div>
        <div className="lc-memory-body">
          stream-consciousness · v0.4 · feed coming soon
        </div>
      </div>
    </div>
  );
}
